<template>
  <div class="soc-smart-guide-panel">
    <div v-if="!sessionActive" class="guide-start">
      <p class="guide-lead">{{ $t('route.socSmartGuideLead') }}</p>
      <p v-if="netStatsLine" class="guide-honesty muted">{{ netStatsLine }}</p>
      <p class="guide-honesty muted">
        {{ guideHonestyZh || $t('route.socSmartGuideLead') }}
        <el-tag v-if="guideClass" size="mini" type="info" effect="plain" style="margin-left:6px">{{ guideClass }}</el-tag>
      </p>
      <div class="guide-intent-row">
        <el-input
          v-model="intentText"
          size="mini"
          :placeholder="$t('route.socSmartGuideIntentPlaceholder')"
          @keyup.enter.native="resolveIntent"
        />
        <el-button type="primary" size="mini" :loading="loading" @click="resolveIntent">{{ $t('route.socSmartGuideStart') }}</el-button>
      </div>
      <div v-if="clarifyQuestions.length" class="guide-clarify">
        <div v-for="q in clarifyQuestions" :key="q.id" class="guide-clarify-q">
          <p>{{ q.promptZh }}</p>
          <el-button
            v-for="opt in (q.options || [])"
            :key="q.id + '-' + opt.value"
            size="mini"
            @click="answerClarify(q.id, opt.value)"
          >{{ opt.labelZh || opt.value }}</el-button>
        </div>
      </div>
      <div v-if="proxyStoreNeeded" class="guide-store-row">
        <el-input v-model="proxyStoreId" size="mini" :placeholder="$t('route.socSmartGuideProxyStore')" />
      </div>
      <div v-if="noticeList.length" class="guide-unfinished">
        <p class="guide-unfinished-title">{{ $t('route.socSmartGuideNotices') }}（{{ noticeList.length }}）</p>
        <div
          v-for="n in noticeList"
          :key="'nt-' + n.id"
          class="guide-unfinished-row"
        >
          <div class="guide-unfinished-meta">
            <strong>{{ n.titleZh }}</strong>
            <span class="muted"> · {{ noticeKindLabel(n.kind) }}</span>
          </div>
          <div class="guide-unfinished-actions">
            <el-button type="text" size="mini" :loading="loading" @click="ackNotice(n.id)">{{ $t('route.socSmartGuideAck') }}</el-button>
          </div>
        </div>
      </div>
      <div v-if="unfinishedList.length" class="guide-unfinished">
        <p class="guide-unfinished-title">{{ $t('route.socSmartGuideMyProcesses') }}（{{ unfinishedList.length }}）</p>
        <div
          v-for="u in unfinishedList"
          :key="'uf-' + (u.refType || 'g') + '-' + (u.refId || u.sessionId)"
          class="guide-unfinished-row"
        >
          <div class="guide-unfinished-meta">
            <strong>{{ u.titleZh || u.playbookId || u.processId }}</strong>
            <span class="muted"> · {{ processStatusLabel(u) }} · {{ u.currentStepTitleZh || ('步骤 ' + ((u.currentStepIndex || 0) + 1)) }}</span>
          </div>
          <div class="guide-unfinished-actions">
            <el-button type="primary" size="mini" :loading="loading" @click="openInboxItem(u)">{{ inboxActionLabel(u) }}</el-button>
            <el-button v-if="u.sessionId" size="mini" :loading="loading" @click="startGuide(u.playbookId, true)">重新开始</el-button>
            <el-button v-if="u.sessionId" type="text" size="mini" :loading="loading" @click="discardUnfinished(u.sessionId)">{{ $t('route.socSmartGuideDelete') }}</el-button>
          </div>
        </div>
      </div>
      <el-collapse class="guide-catalog">
        <el-collapse-item :title="$t('route.socSmartGuideCatalog')" name="catalog">
          <div class="guide-role-row">
            <span class="guide-role-label">{{ $t('route.socSmartGuideRolePreview') }}</span>
            <el-radio-group v-model="previewRole" size="mini" @change="loadPlaybooks">
              <el-radio-button label="">{{ $t('route.socSmartGuideRoleCurrent') }}</el-radio-button>
              <el-radio-button label="store_manager">店长</el-radio-button>
              <el-radio-button label="store_clerk">店员</el-radio-button>
              <el-radio-button label="brand_ops">品牌</el-radio-button>
              <el-radio-button label="platform_admin">超管</el-radio-button>
            </el-radio-group>
          </div>
          <div class="guide-start-actions">
            <el-button
              v-for="pb in activePlaybooks"
              :key="pb.playbookId"
              :type="pb.accessLevel === 'primary' ? 'primary' : 'default'"
              size="small"
              :loading="loading"
              @click="startGuide(pb.playbookId)"
            >
              {{ accessMark(pb.accessLevel) }}{{ pb.titleZh || pb.playbookId }}
            </el-button>
            <el-button
              v-if="!activePlaybooks.length"
              type="primary"
              size="small"
              :loading="loading"
              @click="startGuide('store_manager_dispenser_r1')"
            >
              开始：店长 · 饮料机
            </el-button>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
    <div v-else class="guide-active">
      <div class="guide-head">
        <strong>{{ titleZh }}</strong>
        <el-tag size="mini" type="success">{{ $t('route.socAssistantTabGuide') }}</el-tag>
      </div>
      <p v-if="processEntryZh || processExitZh" class="muted">
        {{ $t('route.socSmartGuideEntry') }}：{{ processEntryZh }}
        <span v-if="processExitZh"> · {{ $t('route.socSmartGuideExit') }}：{{ processExitZh }}</span>
      </p>
      <p v-if="processDemandZh || processResultZh" class="muted">
        <span v-if="processDemandZh">{{ $t('route.socSmartGuideDemand') }}：{{ processDemandZh }}</span>
        <span v-if="processResultZh"> · {{ $t('route.socSmartGuideResult') }}：{{ processResultZh }}</span>
      </p>
      <div v-if="needSlots" class="guide-clarify">
        <p class="guide-instruction">{{ $t('route.socSmartGuideSlotsHint') }}</p>
        <div v-for="q in missingSlots" :key="'slot-' + q.slotId" class="guide-clarify-q">
          <p>{{ q.promptZh }}</p>
          <el-button
            v-for="opt in (q.options || [])"
            :key="q.slotId + '-' + opt.value"
            size="mini"
            @click="answerSlot(q.slotId, opt.value)"
          >{{ opt.labelZh || opt.value }}</el-button>
          <div v-if="!(q.options && q.options.length)" class="guide-intent-row">
            <el-input v-model="slotDraft[q.slotId]" size="mini" :placeholder="q.promptZh" />
            <el-button type="primary" size="mini" :loading="loading" @click="answerSlot(q.slotId, slotDraft[q.slotId])">{{ $t('route.socSmartGuideSlotSubmit') }}</el-button>
          </div>
        </div>
        <p class="muted">{{ formFallbackZh }}</p>
      </div>
      <soc-process-rail :nodes="guideRailNodes" @select="onGuideRailSelect" />
      <soc-step-coach
        v-if="currentStep && !needSlots"
        class="guide-coach"
        :step-number="guideCoachStepNumber"
        :title="guideCoachTitle"
        :hint="currentStep.instructionZh || currentStep.appInstructionZh || ''"
        :scroll-on-show="false"
        button-size="mini"
        :actions="guideCoachActions"
        @action="onGuideCoachAction"
      />
      <div v-if="currentStep" class="guide-current">
        <p
          v-if="boundMachineCode && (currentStep.stepId === 'verify_one_produce' || currentStep.stepId === 'clerk_verify_produce')"
          class="guide-sn"
        >
          验收 SN：{{ boundMachineCode }}（点「去操作此页」会自动带上）
        </p>
        <p v-if="verifyReasonZh" class="guide-verify" :class="{ ok: verifyPassed, bad: !verifyPassed }">
          {{ verifyReasonZh }}
        </p>
        <div class="guide-actions">
          <el-button v-if="!needSlots" type="primary" size="mini" @click="goCurrentPage">去操作此页</el-button>
          <el-button
            v-if="currentStep && currentStep.appAction === 'sim_xt90_brew'"
            type="success"
            size="mini"
            :loading="loading"
            @click="assistSimBrew"
          >模拟点饮验收</el-button>
          <el-button size="mini" :loading="loading" @click="refresh">检查是否完成</el-button>
          <el-button size="mini" type="text" @click="cancelGuide">{{ $t('route.socSmartGuideDelete') }}</el-button>
        </div>
      </div>
      <p v-if="completedBanner" class="guide-done">已完成引导闭环，可关闭。</p>
    </div>
    <soc-work-order-togo-dialog
      :visible="togoVisible"
      :payload="togoPayload"
      @close="togoVisible = false"
      @jump="onTogoJump"
      @voice="onTogoVoice"
    />
  </div>
</template>

<script>
import {
  fetchSmartGuideHonestyStatus,
  fetchSmartGuidePlaybooks,
  fetchSmartGuideProcessNet,
  resolveSmartGuideIntent,
  intakeProcessNet,
  startSmartGuideSession,
  fetchActiveSmartGuideSession,
  fetchUnfinishedSmartGuideSessions,
  resumeSmartGuideSession,
  refreshSmartGuideSession,
  cancelSmartGuideSession,
  assistSmartGuideSession,
  fetchProcessNetInbox,
  ackProcessNetNotice,
  fillSmartGuideSlots,
  fetchWorkOrderTogo
} from '@/api/socSmartGuide'
import { leoOperatorAssistantBus } from '@/utils/leoOperatorAssistant'
import { takePendingSmartGuidePlaybook } from '@/utils/startCommerceOpsUnifyPipeline'
import SocWorkOrderTogoDialog from '@/components/SocWorkOrderTogoDialog'
import SocProcessRail from '@/components/SocProcessRail'
import SocStepCoach from '@/components/SocStepCoach'
import { resolveTogoJump } from '@/utils/socWorkOrderTogo'
import { startProcessNetTogoVoice } from '@/utils/startBrandOrderTogoPipeline'

export default {
  name: 'SocSmartGuidePanel',
  components: { SocWorkOrderTogoDialog, SocProcessRail, SocStepCoach },
  data () {
    return {
      loading: false,
      sessionId: null,
      titleZh: '',
      steps: [],
      currentStep: null,
      verifyPassed: false,
      verifyReasonZh: '',
      completedBanner: false,
      pollTimer: null,
      boundMachineCode: '',
      activePlaybooks: [],
      previewRole: '',
      intentText: '',
      clarifyQuestions: [],
      clarifyAnswers: {},
      pendingEntryStepId: null,
      pendingPathHint: null,
      proxyStoreId: '',
      proxyStoreNeeded: false,
      guideHonestyZh: '',
      guideClass: '',
      unfinishedList: [],
      noticeList: [],
      needSlots: false,
      missingSlots: [],
      formFallbackZh: '',
      slotDraft: {},
      netUnitCount: 0,
      netProcessCount: 0,
      netEdgeCount: 0,
      togoVisible: false,
      togoPayload: null,
      processEntryZh: '',
      processDemandZh: '',
      processExitZh: '',
      processResultZh: ''
    }
  },
  computed: {
    sessionActive () {
      return !!this.sessionId
    },
    netStatsLine () {
      if (!this.netUnitCount) return ''
      return this.$t('route.socSmartGuideNetStats', {
        units: this.netUnitCount,
        processes: this.netProcessCount,
        edges: this.netEdgeCount
      })
    },
    guideRailNodes () {
      var list = this.steps || []
      var out = []
      var i
      for (i = 0; i < list.length; i++) {
        var s = list[i]
        var st = 'todo'
        if (s.status === 'DONE') st = 'done'
        else if (s.status === 'CURRENT') st = 'current'
        out.push({
          id: s.stepId,
          title: s.titleZh || s.stepId,
          routePath: s.routePath || '',
          done: st === 'done',
          current: st === 'current',
          stuck: false,
          statusLabel: st === 'done' ? '完成' : (st === 'current' ? '进行中' : '未开始'),
          locked: false,
          raw: s
        })
      }
      return out
    },
    guideCoachStepNumber () {
      var i
      for (i = 0; i < this.guideRailNodes.length; i++) {
        if (this.guideRailNodes[i].current) return i + 1
      }
      return 1
    },
    guideCoachTitle () {
      var s = this.currentStep
      return s ? ('请完成：' + (s.titleZh || s.stepId)) : '请依序完成'
    },
    guideCoachActions () {
      var list = []
      if (this.currentStep && this.currentStep.routePath) {
        list.push({ id: 'go_page', label: '去操作此页', type: 'primary' })
      }
      list.push({ id: 'refresh_check', label: '检查是否完成', type: list.length ? 'default' : 'primary' })
      return list
    }
  },
  mounted () {
    this.loadHonesty()
    this.loadPlaybooks()
    this.loadProcessNet()
    this.loadActive()
    this.loadUnfinished()
    leoOperatorAssistantBus.$on('smart-guide-changed', this.refreshQuiet)
    leoOperatorAssistantBus.$on('start-smart-guide-playbook', this.onStartPlaybookRequest)
    const pending = takePendingSmartGuidePlaybook()
    if (pending) {
      this.$nextTick(() => this.onStartPlaybookRequest(pending))
    }
  },
  beforeDestroy () {
    this.stopPoll()
    leoOperatorAssistantBus.$off('smart-guide-changed', this.refreshQuiet)
    leoOperatorAssistantBus.$off('start-smart-guide-playbook', this.onStartPlaybookRequest)
  },
  methods: {
    accessMark (level) {
      if (level === 'primary') return '● '
      if (level === 'proxy') return '○ '
      if (level === 'limited') return '△ '
      return ''
    },
    onStartPlaybookRequest (payload) {
      if (!payload) return
      if (typeof payload === 'string') {
        this.startGuide(payload)
        return
      }
      if (payload.entryStepId) this.pendingEntryStepId = payload.entryStepId
      if (payload.pathHint) this.pendingPathHint = payload.pathHint
      this.startGuide(payload.playbookId)
    },
    stepMark (s) {
      if (s.status === 'DONE') return '✓'
      if (s.status === 'CURRENT') return '→'
      return '·'
    },
    onGuideRailSelect (node) {
      if (!node) return
      if (node.raw) this.goStepPage(node.raw)
      else if (node.routePath) this.$router.push({ path: node.routePath })
    },
    onGuideCoachAction (action) {
      var id = action && (action.id || action)
      if (id === 'go_page') {
        this.goCurrentPage()
        return
      }
      if (id === 'refresh_check') this.refresh()
    },
    async loadHonesty () {
      try {
        const res = await fetchSmartGuideHonestyStatus()
        const d = (res && res.data) || res || {}
        this.guideHonestyZh = d.honestyZh || ''
        this.guideClass = d.guideClass || ''
      } catch (e) {
        this.guideHonestyZh = this.$t('route.socSmartGuideLead')
        this.guideClass = 'FIXED_PLAYBOOK'
      }
    },
    async loadProcessNet () {
      try {
        const res = await fetchSmartGuideProcessNet()
        const d = (res && res.data) || res || {}
        this.netUnitCount = Number(d.unitCount || 0)
        this.netProcessCount = Number(d.processCount || 0)
        this.netEdgeCount = Number(d.edgeCount || 0)
      } catch (e) {
        this.netUnitCount = 0
        this.netProcessCount = 0
        this.netEdgeCount = 0
      }
    },
    async loadPlaybooks () {
      try {
        const res = await fetchSmartGuidePlaybooks(this.previewRole || undefined)
        const list = (res && res.data) || []
        this.activePlaybooks = list.filter(function (p) {
          return p && String(p.status || '').toLowerCase() === 'active'
        })
        const first = this.activePlaybooks[0]
        if (first && first.honestyZh && !this.guideHonestyZh) {
          this.guideHonestyZh = first.honestyZh
          this.guideClass = first.guideClass || 'FIXED_PLAYBOOK'
        }
      } catch (e) {
        this.activePlaybooks = [
          { playbookId: 'store_manager_dispenser_r1', titleZh: '店长 · 饮料机', status: 'active', accessLevel: 'primary' },
          { playbookId: 'store_clerk_dispenser_r2', titleZh: '店员 · 饮料机', status: 'active', accessLevel: 'proxy' },
          { playbookId: 'store_manager_tea_r2', titleZh: '店长 · 茶机', status: 'active', accessLevel: 'primary' },
          { playbookId: 'store_manager_coffee_r2', titleZh: '店长 · 咖啡机', status: 'active', accessLevel: 'primary' },
          { playbookId: 'brand_ops_org_device_r2', titleZh: '品牌 · 组织设备', status: 'active', accessLevel: 'proxy' },
          { playbookId: 'ops_inventory_r2', titleZh: '营运 · 库存闭环', status: 'active', accessLevel: 'primary' },
          { playbookId: 'platform_admin_map_r2', titleZh: '超管 · 全图代开', status: 'active', accessLevel: 'proxy' },
          { playbookId: 'platform_admin_global_device_auth_r2', titleZh: '超管 · 全球认证', status: 'active', accessLevel: 'proxy' },
          { playbookId: 'platform_admin_device_order_lifecycle_r2', titleZh: '总部 · 设备主链', status: 'active', accessLevel: 'proxy' },
          { playbookId: 'brand_ops_device_order_lifecycle_r2', titleZh: '品牌 · 设备主链', status: 'active', accessLevel: 'primary' },
          { playbookId: 'factory_ops_device_order_lifecycle_r2', titleZh: '工厂 · 设备主链', status: 'active', accessLevel: 'primary' },
          { playbookId: 'ops_cloud_iris_r2', titleZh: '营运 · SOC雲瞳', status: 'active', accessLevel: 'primary' },
          { playbookId: 'cloud_iris_commerce_r2', titleZh: '品牌 · 雲瞳从零到付费', status: 'active', accessLevel: 'primary' }
        ]
      }
    },
    async resolveIntent () {
      this.loading = true
      try {
        const payload = Object.assign({ utterance: this.intentText || '' }, this.clarifyAnswers)
        if (this.previewRole) payload.guideRole = this.previewRole
        var data = null
        try {
          const intakeRes = await intakeProcessNet(payload)
          data = intakeRes && intakeRes.data
        } catch (intakeErr) {
          const res = await resolveSmartGuideIntent(payload)
          data = res && res.data
        }
        this.clarifyQuestions = (data && data.clarify) || []
        if (data && data.sessionId && (data.started || data.active)) {
          this.applySession(data)
          if (data.needSlots) {
            this.$message.info(this.$t('route.socSmartGuideSlotsHint'))
          } else if (!(data && data.needSlots)) {
            this.goCurrentPage()
          }
        } else if (data && data.resolved && data.playbookId) {
          this.pendingEntryStepId = data.entryStepId || null
          this.pendingPathHint = data.pathHint || this.clarifyAnswers.catalogPath || this.clarifyAnswers.pathHint || null
          this.proxyStoreNeeded = String(data.playbookId).indexOf('store_clerk_') === 0
          await this.startGuide(data.playbookId)
        } else if (!this.clarifyQuestions.length) {
          this.$message.warning('无法解析需求，请再说一次或从进程目录选')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '意图解析失败')
      } finally {
        this.loading = false
      }
    },
    answerClarify (id, value) {
      this.$set(this.clarifyAnswers, id, value)
      if (id === 'role') this.clarifyAnswers.guideRole = value
      if (id === 'deviceFamily') this.clarifyAnswers.deviceFamily = value
      if (id === 'goal') this.clarifyAnswers.goal = value
      if (id === 'catalogPath' || id === 'pathHint') {
        this.clarifyAnswers.pathHint = value
        this.clarifyAnswers.catalogPath = value
        this.pendingPathHint = value
      }
      this.resolveIntent()
    },
    applyHandoffOrSession (data) {
      if (data && data.completedNow && data.nextSession) {
        this.applySession(data.nextSession)
        return
      }
      this.applySession(data)
    },
    applySession (data) {
      if (!data || !data.active) {
        this.sessionId = null
        this.steps = []
        this.currentStep = null
        this.completedBanner = false
        this.needSlots = false
        this.missingSlots = []
        this.stopPoll()
        return
      }
      this.sessionId = data.sessionId
      this.titleZh = data.titleZh || ''
      this.steps = data.steps || []
      this.currentStep = data.currentStep || null
      this.boundMachineCode = data.boundMachineCode
        || (data.context && data.context.imie)
        || ''
      if (this.currentStep) {
        this.verifyPassed = !!this.currentStep.verifyPassed
        this.verifyReasonZh = this.currentStep.verifyReasonZh || ''
      }
      this.completedBanner = data.status === 'COMPLETED' || !!data.completedNow
      this.needSlots = !!data.needSlots
      this.missingSlots = data.missingSlots || []
      this.formFallbackZh = data.formFallbackZh || ''
      this.processEntryZh = data.entryZh || ''
      this.processDemandZh = data.demandZh || ''
      this.processExitZh = data.exitZh || ''
      this.processResultZh = data.resultZh || ''
      const draft = {}
      this.missingSlots.forEach((q) => {
        draft[q.slotId] = (this.slotDraft && this.slotDraft[q.slotId]) || ''
      })
      this.slotDraft = draft
      if (data.active && !this.completedBanner) {
        this.startPoll()
      } else {
        this.stopPoll()
      }
    },
    async loadActive () {
      try {
        const res = await fetchActiveSmartGuideSession()
        this.applySession(res && res.data)
      } catch (e) {
        // ignore
      }
    },
    async loadUnfinished () {
      try {
        const res = await fetchProcessNetInbox()
        const data = (res && res.data) || {}
        this.unfinishedList = data.items || data.unfinished || []
        this.noticeList = data.notices || []
      } catch (e) {
        try {
          const res = await fetchUnfinishedSmartGuideSessions()
          const data = (res && res.data) || {}
          this.unfinishedList = data.unfinished || []
          this.noticeList = []
        } catch (e2) {
          this.unfinishedList = []
          this.noticeList = []
        }
      }
    },
    noticeKindLabel (kind) {
      if (kind === 'DONE_WRONG') return this.$t('route.socSmartGuideNoticeWrong')
      if (kind === 'DUE') return this.$t('route.socSmartGuideNoticeDue')
      return this.$t('route.socSmartGuideNoticeNotDone')
    },
    processStatusLabel (u) {
      if (!u) return this.$t('route.socSmartGuideInProgress')
      if (u.status === 'PAUSED') return this.$t('route.socSmartGuidePaused')
      if (typeof u.toGoCount === 'number' && u.toGoCount > 0) {
        return this.$t('route.socProcessNetTogoRemain', { n: u.toGoCount })
      }
      return this.$t('route.socSmartGuideInProgress')
    },
    inboxActionLabel (u) {
      if (u && u.sessionId) return this.$t('route.socSmartGuideContinue')
      if (u && (u.togoKind || u.kind === 'SYSTEM')) return this.$t('route.socProcessNetTogoTitle')
      return this.$t('route.socSmartGuideOpenPage')
    },
    async openInboxItem (u) {
      if (!u) return
      if (u.sessionId) {
        await this.resumeUnfinished(u.sessionId)
        return
      }
      if (u.kind === 'SYSTEM' && u.refId) {
        await this.openInboxTogo(u)
        return
      }
      if (u.playbookId) {
        await this.startGuide(u.playbookId)
        return
      }
      if (u.routePath) {
        this.$router.push({ path: u.routePath })
      }
    },
    async openInboxTogo (u) {
      this.loading = true
      try {
        const res = await fetchWorkOrderTogo(u.togoKind || u.refType, u.refId)
        const data = (res && res.data) || {}
        this.togoPayload = data
        if (data.found === false && u.routePath) {
          this.$router.push({ path: u.routePath })
          return
        }
        this.togoVisible = true
      } catch (e) {
        if (u.routePath) this.$router.push({ path: u.routePath })
        else this.$message.error(this.$t('route.socProcessNetTogoLoadFail'))
      } finally {
        this.loading = false
      }
    },
    onTogoJump (step) {
      this.togoVisible = false
      if (step && step.routePath) {
        this.$router.push({ path: step.routePath, query: step.query || {} })
        return
      }
      var jump = resolveTogoJump(step)
      if (jump && jump.kind === 'route') {
        this.$router.push({ path: jump.path, query: jump.query || {} })
      }
    },
    onTogoVoice (step) {
      this.togoVisible = false
      startProcessNetTogoVoice(step && step.playbookId, step && step.entryStepId)
    },
    async ackNotice (id) {
      if (!id) return
      this.loading = true
      try {
        await ackProcessNetNotice(id)
        await this.loadUnfinished()
      } catch (e) {
        this.$message.error((e && e.message) || '无法标记已知悉')
      } finally {
        this.loading = false
      }
    },
    async answerSlot (slotId, value) {
      if (!this.sessionId || !slotId || value == null || String(value).trim() === '') return
      this.loading = true
      try {
        const res = await fillSmartGuideSlots(this.sessionId, { [slotId]: value })
        this.applySession(res && res.data)
        if (!(res && res.data && res.data.needSlots)) {
          this.goCurrentPage()
        }
        await this.loadUnfinished()
      } catch (e) {
        this.$message.error((e && e.message) || '槽位未写入')
      } finally {
        this.loading = false
      }
    },
    async resumeUnfinished (sessionId) {
      if (!sessionId) return
      this.loading = true
      try {
        const res = await resumeSmartGuideSession(sessionId)
        this.applySession(res && res.data)
        this.goCurrentPage()
        await this.loadUnfinished()
        leoOperatorAssistantBus.$emit('smart-guide-changed')
        this.$message.success('已继续未完成引导')
      } catch (e) {
        this.$message.error((e && e.message) || '无法继续')
      } finally {
        this.loading = false
      }
    },
    async discardUnfinished (sessionId) {
      if (!sessionId) return
      try {
        await this.$confirm(
          this.$t('route.socSmartGuideDeleteConfirm'),
          this.$t('route.socSmartGuideDelete'),
          { type: 'warning' }
        )
      } catch (e) {
        return
      }
      this.loading = true
      try {
        await cancelSmartGuideSession(sessionId)
        if (String(this.sessionId) === String(sessionId)) {
          this.applySession(null)
        }
        await this.loadUnfinished()
        leoOperatorAssistantBus.$emit('smart-guide-changed')
        this.$message.success(this.$t('route.socSmartGuideDeleted'))
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('route.socSmartGuideDeleteFail'))
      } finally {
        this.loading = false
      }
    },
    async startGuide (playbookId, forceRestart) {
      const id = playbookId || 'store_manager_dispenser_r1'
      if (String(id).indexOf('store_clerk_') === 0) {
        this.proxyStoreNeeded = true
      }
      const opts = {}
      if (forceRestart) opts.forceRestart = true
      if (this.pendingEntryStepId) opts.entryStepId = this.pendingEntryStepId
      if (this.proxyStoreId) opts.storeId = this.proxyStoreId
      if (this.pendingPathHint) opts.pathHint = this.pendingPathHint
      else if (this.clarifyAnswers.pathHint) opts.pathHint = this.clarifyAnswers.pathHint
      else if (String(id) === 'store_manager_dispenser_r1') opts.pathHint = 'auto'
      this.loading = true
      try {
        const res = await startSmartGuideSession(id, opts)
        this.pendingEntryStepId = null
        this.pendingPathHint = null
        this.clarifyQuestions = []
        this.applySession(res && res.data)
        const data = res && res.data
        if (!(data && data.needSlots)) {
          this.goCurrentPage()
        }
        await this.loadUnfinished()
        leoOperatorAssistantBus.$emit('smart-guide-changed')
        this.$message.success(data && data.resumed ? '已继续未完成引导' : '智能引导已开始')
      } catch (e) {
        const msg = (e && e.message) || '无法开始引导'
        if (String(msg).indexOf('STORE_REQUIRED') >= 0) {
          this.proxyStoreNeeded = true
          this.$message.warning('代跑店员线请先填门店 ID')
        } else if (String(msg).indexOf('SMART_GUIDE_ROLE_DENIED') >= 0) {
          this.$message.warning('当前账号角色不能开这条引导线（烧钱审计需平台超管或品牌运维）')
        } else {
          this.$message.error(msg)
        }
      } finally {
        this.loading = false
      }
    },
    goCurrentPage () {
      this.goStepPage(this.currentStep)
    },
    goStepPage (step) {
      const routePath = step && step.routePath
      if (!routePath) return
      const query = {}
      if (routePath.indexOf('simulator-xt90') >= 0 && this.boundMachineCode) {
        query.machineCode = this.boundMachineCode
      }
      if (this.$route.path !== routePath || (query.machineCode && this.$route.query.machineCode !== query.machineCode)) {
        this.$router.push({ path: routePath, query: query })
      }
    },
    async assistSimBrew () {
      if (!this.sessionId) return
      this.loading = true
      try {
        const res = await assistSmartGuideSession(this.sessionId, 'sim_xt90_brew')
        this.applySession(res && res.data)
        leoOperatorAssistantBus.$emit('smart-guide-changed')
        this.$message.success('已触发模拟点饮')
      } catch (e) {
        this.$message.error((e && e.message) || '模拟点饮失败')
      } finally {
        this.loading = false
      }
    },
    async refresh () {
      if (!this.sessionId) return
      this.loading = true
      try {
        const res = await refreshSmartGuideSession(this.sessionId)
        const data = res && res.data
        if (data && data.verifyReasonZh) {
          this.verifyPassed = !!data.verifyPassed
          this.verifyReasonZh = data.verifyReasonZh
        }
        this.applyHandoffOrSession(data)
        leoOperatorAssistantBus.$emit('smart-guide-changed')
        if (data && data.handoffStarted && data.nextSession) {
          this.$message.success(data.handoffZh || this.$t('route.socSmartGuideHandoff'))
          if (!(data.nextSession && data.nextSession.needSlots)) {
            this.goCurrentPage()
          }
        } else if (data && data.advanced && !data.needSlots) {
          this.$message.success('本步已完成，进入下一步')
          this.goCurrentPage()
        } else if (data && data.completedNow) {
          this.$message.success('引导全部完成')
        } else if (data && data.verifyPassed === false) {
          this.$message.warning(data.verifyReasonZh || '尚未完成')
        }
      } catch (e) {
        this.$message.error((e && e.message) || '检查失败')
      } finally {
        this.loading = false
      }
    },
    async cancelGuide () {
      if (!this.sessionId) return
      try {
        await this.$confirm(
          this.$t('route.socSmartGuideDeleteConfirm'),
          this.$t('route.socSmartGuideDelete'),
          { type: 'warning' }
        )
      } catch (e) {
        return
      }
      try {
        await cancelSmartGuideSession(this.sessionId)
      } catch (e) {
        // still clear local
      }
      this.applySession(null)
      await this.loadUnfinished()
      leoOperatorAssistantBus.$emit('smart-guide-changed')
      this.$message.info(this.$t('route.socSmartGuideDeleted'))
    },
    startPoll () {
      this.stopPoll()
      const self = this
      this.pollTimer = setInterval(function () {
        if (self.sessionId && !self.loading) {
          self.refreshQuiet()
        }
      }, 8000)
    },
    stopPoll () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async refreshQuiet () {
      if (!this.sessionId) return
      try {
        const res = await refreshSmartGuideSession(this.sessionId)
        const data = res && res.data
        const prevIdx = this.currentStep && this.currentStep.index
        this.applyHandoffOrSession(data)
        if (data && data.handoffStarted && data.nextSession) {
          this.$message.success(data.handoffZh || this.$t('route.socSmartGuideHandoff'))
          if (!(data.nextSession && data.nextSession.needSlots)) {
            this.goCurrentPage()
          }
        } else if (data && data.advanced && !(data && data.needSlots)) {
          this.$message.success('本步已完成，进入下一步')
          this.goCurrentPage()
        } else if (data && data.completedNow) {
          this.$message.success('引导全部完成')
        } else if (data && data.currentStep && data.currentStep.index !== prevIdx && !(data && data.needSlots)) {
          this.goCurrentPage()
        }
      } catch (e) {
        // quiet
      }
    }
  }
}
</script>

<style scoped>
.soc-smart-guide-panel {
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.guide-start,
.guide-active {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.guide-lead {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--sap-color-text, #303133);
}
.guide-honesty {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--sap-color-text-secondary, #909399);
}
.guide-role-row,
.guide-intent-row,
.guide-store-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
}
.guide-role-label {
  font-size: 12px;
  color: #606266;
}
.guide-intent-row .el-input {
  flex: 1;
  min-width: 180px;
}
.guide-clarify {
  margin: 0;
  padding: 10px 12px;
  background: var(--sap-color-fill, #f8fafc);
  border-radius: 6px;
  border: 1px dashed var(--sap-color-border, #e4e7ed);
}
.guide-clarify-q p {
  margin: 0 0 6px;
  font-size: 12px;
}
.guide-clarify-q .el-button {
  margin: 0 6px 6px 0;
}
.guide-start-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.guide-unfinished {
  margin: 0;
  padding: 10px 12px;
  background: var(--sap-color-fill, #f8fafc);
  border-radius: 6px;
  border: 1px solid var(--sap-color-border, #e4e7ed);
}
.guide-catalog {
  border: none;
}
.guide-catalog >>> .el-collapse-item__header {
  font-size: 13px;
  height: auto;
  line-height: 1.4;
  padding: 4px 0;
  background: transparent;
}
.guide-unfinished-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--sap-color-text, #303133);
}
.guide-unfinished-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-top: 1px dashed #ebeef5;
}
.guide-unfinished-row:first-of-type {
  border-top: none;
}
.guide-unfinished-meta {
  font-size: 12px;
  line-height: 1.4;
}
.guide-unfinished-meta .muted,
.muted {
  color: #909399;
  font-weight: normal;
}
.guide-unfinished-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.guide-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  color: var(--sap-color-text, #303133);
}
.guide-coach {
  margin: 8px 0 10px;
}
.guide-steps {
  margin: 0 0 10px;
  padding-left: 18px;
  font-size: 12px;
  line-height: 1.7;
  color: #606266;
}
.guide-steps .current {
  color: #409eff;
  font-weight: 600;
}
.guide-steps .done {
  color: #67c23a;
}
.guide-steps .clickable {
  cursor: pointer;
}
.step-mark {
  display: inline-block;
  width: 14px;
}
.guide-instruction {
  margin: 0 0 6px;
  font-size: 13px;
  line-height: 1.5;
}
.guide-sn {
  margin: 0 0 6px;
  font-size: 12px;
  color: #909399;
}
.guide-verify {
  margin: 0 0 8px;
  font-size: 12px;
}
.guide-verify.ok {
  color: #67c23a;
}
.guide-verify.bad {
  color: #e6a23c;
}
.guide-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.guide-done {
  margin: 10px 0 0;
  color: #67c23a;
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/components/LeoKnowledge/SocSmartGuidePanel.vue